<template>
  <SectionFrame v-if="shown || inStudio" :section="section" :index="index" :heading="heading" label="Find us">
    <div v-if="shown" class="s2-find" :data-layout="layout">
      <div class="s2-find-text mo-reveal">
        <address class="t-body s2-address">
          <span v-for="line in business.addressLines" :key="line">{{ line }}</span>
        </address>
        <p v-if="business.phone" class="t-body"><a class="t-link s2-strong" :href="`tel:${business.phone.replace(/\s/g, '')}`">{{ business.phone }}</a></p>
        <p v-if="section.parkingNote" class="s2-note">{{ section.parkingNote }}</p>
        <p v-if="section.showHours && hoursLine" class="s2-note">{{ hoursLine }}</p>
        <p v-if="section.showDirections !== false" class="s2-find-cta">
          <a class="t-btn" :href="directionsUrl" target="_blank" rel="noopener">Get directions</a>
        </p>
      </div>
      <div v-if="layout !== 'address'" class="s2-map t-photo mo-reveal" :class="layout === 'big-map' ? 's2-map--big' : ''">
        <!-- The map loads only when asked: no third-party request until then. -->
        <iframe v-if="mapOn && mapSrc" :src="mapSrc" :title="`Map of ${business.addressLines[0] || 'our address'}`" loading="lazy"
                referrerpolicy="no-referrer"></iframe>
        <button v-else type="button" class="s2-map-btn" @click="mapOn = true" :aria-label="'Show the map'">
          <span class="s2-pin" aria-hidden="true"></span>
          <span class="t-btn t-btn--ghost">Show the map</span>
        </button>
      </div>
    </div>
    <StudioGap v-else>Add your address (Business settings), then it shows here with directions.</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {computed, ref} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {layoutOf, weekRows} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["map-address", "address", "big-map"]));
const heading = computed(() => props.section.heading ?? "Find us");
const business = computed(() => props.data.business || {addressLines: []});
const shown = computed(() => (business.value.addressLines || []).length > 0);
const mapOn = ref(false);
const query = computed(() => encodeURIComponent((business.value.addressLines || []).join(", ")));
const directionsUrl = computed(() => `https://www.google.com/maps/dir/?api=1&destination=${query.value}`);
const mapSrc = computed(() => {
  const {lat, lng} = business.value;
  if (typeof lat === "number" && typeof lng === "number") {
    const d = 0.006;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - d}%2C${lat - d}%2C${lng + d}%2C${lat + d}&layer=mapnik&marker=${lat}%2C${lng}`;
  }
  return `https://www.google.com/maps?q=${query.value}&output=embed`;
});
const hoursLine = computed(() => weekRows(props.data.hours).map((r) => `${r.days} ${r.text}`).join(" · "));
</script>

<style>
.s2-find { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr); gap: var(--t-gap); align-items: stretch; }
.s2-find[data-layout="address"] { grid-template-columns: minmax(0, 1fr); }
.s2-find[data-layout="big-map"] { grid-template-columns: minmax(0, 1fr); }
.s2-find[data-layout="big-map"] .s2-map { order: -1; }
@media (max-width: 760px) { .s2-find { grid-template-columns: minmax(0, 1fr); } }
.s2-address { font-style: normal; display: flex; flex-direction: column; gap: .15rem; font-size: 1.05rem; }
.s2-strong { font-weight: 700; font-size: 1.1rem; }
.s2-find-cta { margin-top: 1.25rem; }
.s2-map { position: relative; min-height: 280px; background: var(--t-surface-2); }
.s2-map--big { min-height: 420px; }
.s2-map iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.s2-map-btn { position: absolute; inset: 0; width: 100%; border: 0; cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem;
  background: repeating-linear-gradient(0deg, transparent 0 26px, color-mix(in srgb, var(--t-line) 70%, transparent) 26px 27px),
              repeating-linear-gradient(90deg, transparent 0 26px, color-mix(in srgb, var(--t-line) 70%, transparent) 26px 27px), var(--t-surface-2); }
.s2-pin { width: 22px; height: 22px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); background: var(--t-accent); box-shadow: 0 4px 10px rgba(0,0,0,.25); }
</style>
